/**
 * The route a visitor was on, with anything that grants access removed.
 *
 * Performance telemetry wants to know which *kind* of page was slow, not which
 * instance — `/claim/[token]` answers "the booking-link page took 1.2s" just as
 * well as the real URL does, and is the shape RUM tools expect anyway.
 *
 * The reason this exists rather than reporting `location.pathname` directly:
 * `/claim/<token>` carries a bearer credential IN THE PATH. It is valid for 30
 * days, works any number of times, cannot be revoked, and opens the booking it
 * names. `web-vitals-reporter` beacons the URL to `/api/rum`, which writes it as
 * a structured log line for the platform's log aggregator, and PostHog captures
 * it as `$current_url` on its initial pageview. So the unredacted form put a
 * live credential into log storage and a third-party analytics store on every
 * claim-page visit.
 *
 * `/claim` is a server redirect again — `claim/[token]/route.ts` is a route
 * handler that verifies the token, moves it into an httpOnly cookie and
 * redirects, rendering no HTML — so today no client script sees that URL and
 * this prefix cannot fire. It is kept anyway, and deliberately: the entry costs
 * one array element, the guard is worthless only for as long as nothing renders
 * under `/claim`, and the failure mode if that changes and this is missing is a
 * live credential in log storage and a third-party analytics store. A security
 * allowlist is not a "just in case" fallback; it is the thing that has to
 * already be there.
 *
 * Redaction is by allowlist of known-sensitive routes rather than by guessing at
 * what looks like a secret. A heuristic ("redact anything long", "redact
 * UUID-shaped segments") is the kind of thing that quietly stops matching when a
 * token format changes, and nothing fails when it does.
 */

/** Routes whose dynamic segment IS the credential. */
const REDACTED_PATH_PREFIXES = ["/claim"] as const;

/** Query parameters that carry a credential. `?token=` is the password
 *  set/reset link; `/set-password/` strips it from the address bar on mount,
 *  but a beacon can fire before that happens. */
const REDACTED_QUERY_PARAMS = ["token"] as const;

const PLACEHOLDER = "[redacted]";

export function telemetryUrl(pathname: string, search: string): string {
  return redactPath(pathname) + redactSearch(search);
}

function redactPath(pathname: string): string {
  for (const prefix of REDACTED_PATH_PREFIXES) {
    if (pathname === prefix) return prefix;
    if (pathname.startsWith(`${prefix}/`)) return `${prefix}/${PLACEHOLDER}`;
  }
  return pathname;
}

function redactSearch(search: string): string {
  if (!search || search === "?") return "";
  // `URLSearchParams` handles repeats and encoding; a base is required but
  // never read, since only the query is serialised back out.
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  let touched = false;
  for (const name of REDACTED_QUERY_PARAMS) {
    if (params.has(name)) {
      params.set(name, PLACEHOLDER);
      touched = true;
    }
  }
  if (!touched) return search.startsWith("?") ? search : `?${search}`;
  const rebuilt = params.toString();
  return rebuilt ? `?${rebuilt}` : "";
}
